import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import type { Coordinate } from '@/types/properties';
import type { MoreProps } from '@/types/commontypes';
dayjs.extend(duration);
export const SMALL_VALUE_THRESHOLD = 0.001;

export function getDuration(
  elapsedTime: number,
  maxUnit: 'year' | 'month' | 'day' | 'hour' | 'minute' | 'second' | 'millisecond' = 'year',
) {
  const dur = dayjs.duration(elapsedTime);

  const years = dur.years();
  const months = dur.months();
  const days = dur.days();
  const hours = dur.hours();
  const minutes = dur.minutes();
  const seconds = dur.seconds();
  const milliseconds = dur.milliseconds();

  if (maxUnit === 'year') {
    return { years, months, days, hours, minutes, seconds, milliseconds };
  }
  if (maxUnit === 'month') {
    return { years: 0, months: Math.floor(dur.asMonths()), days, hours, minutes, seconds, milliseconds };
  }
  if (maxUnit === 'day') {
    return { years: 0, months: 0, days: Math.floor(dur.asDays()), hours, minutes, seconds, milliseconds };
  }
  if (maxUnit === 'hour') {
    return { years: 0, months: 0, days: 0, hours: Math.floor(dur.asHours()), minutes, seconds, milliseconds };
  }
  if (maxUnit === 'minute') {
    return { years: 0, months: 0, days: 0, hours: 0, minutes: Math.floor(dur.asMinutes()), seconds, milliseconds };
  }
  if (maxUnit === 'second') {
    return { years: 0, months: 0, days: 0, hours: 0, minutes: 0, seconds: Math.floor(dur.asSeconds()), milliseconds };
  }
  return {
    years: 0,
    months: 0,
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    milliseconds: Math.floor(dur.asMilliseconds()),
  };
}

export const calculateBarRange = (moreProps: MoreProps, start: unknown, end: unknown): number => {
  const { xAccessor, plotData } = moreProps;
  const startIndex = plotData.findIndex(d => xAccessor(d) === start);
  const endIndex = plotData.findIndex(d => xAccessor(d) === end);
  const barRange = Math.abs(endIndex - startIndex);
  return barRange;
};

export const timeframeToMinutes = (tf: string): number => {
  const num = parseInt(tf);
  if (tf.includes('m')) return num;
  if (tf.includes('h')) return num * 60;
  if (tf.includes('d')) return num * 1440;
  return 0;
};

/**
 * convert timeframe to milliseconds
 * @param timeframe - '1m', '5m', '15m', '1h', '4h', '1d', '1w', '1M' 등
 * @returns milliseconds
 * @example
 * getTimeframeMilliseconds('1m')  // 60000
 * getTimeframeMilliseconds('1h')  // 3600000
 * getTimeframeMilliseconds('1d')  // 86400000
 */
export const getTimeframeMilliseconds = (timeframe: string): number => {
  const suffix = timeframe.slice(-1); // first letter of timeframe (m, h, d, w, M)
  const count = Number(timeframe.replace(suffix, ''));
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return dayjs.duration(count, suffix as duration.DurationUnitType).asMilliseconds();
};

export const calculateDaysBetweenPoints = (
  bars: number,
  timeframe: string,
): { days: number; hours: number; minutes: number } => {
  const minutesTotal = bars * timeframeToMinutes(timeframe);

  const days = Math.floor(minutesTotal / 1440);
  const hours = Math.floor((minutesTotal % 1440) / 60);
  const minutes = minutesTotal % 60;

  return { days, hours, minutes };
};

export function calculateAngle(
  moreProps: { xScale: (v: number) => number; chartConfig: { yScale: (v: number) => number } },
  startXY: Coordinate,
  endXY: Coordinate,
): number {
  const {
    xScale,
    chartConfig: { yScale },
  } = moreProps;
  if (!Array.isArray(startXY) || startXY.length < 2 || !Array.isArray(endXY) || endXY.length < 2) {
    return 0; // or a safe default
  }

  // Determine left-to-right order based on x-coordinate (data space)
  const [x1, y1] = startXY;
  const [x2, y2] = endXY;

  // Ensure the left point is the starting point
  let from: Coordinate;
  let to: Coordinate;

  if (x1 <= x2) {
    from = [x1, y1];
    to = [x2, y2];
  } else {
    from = [x2, y2];
    to = [x1, y1];
  }

  // Convert data units to pixel coordinates
  const [px1, py1] = [xScale(from[0]), yScale(from[1])];
  const [px2, py2] = [xScale(to[0]), yScale(to[1])];

  // Calculate pixel deltas
  const dx = px2 - px1;
  const dy = py2 - py1;

  // Y-axis in pixel coordinates is usually inverted (top = 0)
  const angleRad = Math.atan2(-dy, dx); // negate dy if y-axis is top-down
  let angleDeg = angleRad * (180 / Math.PI);

  // Optional: Clamp angle to [-90, 90] if needed
  if (angleDeg > 90) angleDeg = 180 - angleDeg;
  if (angleDeg < -90) angleDeg = -(180 + angleDeg);

  return angleDeg;
}

export const calculatePriceRange = (y1: number, y2: number): number => {
  if (y1 > SMALL_VALUE_THRESHOLD && y2 > SMALL_VALUE_THRESHOLD) {
    return round(y2 - y1, 2);
  }
  const scale = Math.max(getScaleFactor(y1), getScaleFactor(y2));
  return (y2 * scale - y1 * scale) / scale; // NO rounding here
};

export const calculatePercentChange = (y1: number, y2: number): number => {
  if (!y1) return 0;

  if (y1 > SMALL_VALUE_THRESHOLD && y2 > SMALL_VALUE_THRESHOLD) {
    return round(((y2 - y1) / y1) * 100, 2);
  }

  if (y1 === 0) return 0;
  const scale = Math.max(getScaleFactor(y1), getScaleFactor(y2));
  return round(((y2 * scale - y1 * scale) / (y1 * scale)) * 100, 2);
};

const getScaleFactor = (value: number): number => {
  if (!value) return 1;

  const exponent = Math.floor(Math.log10(Math.abs(value)));

  // For values < 1, exponent is negative
  // ex: 0.000006 → exponent = -6 → scale = 10^6
  return exponent < 0 ? Math.pow(10, -exponent) : 1;
};

export const round = (value: number, decimals = 2): number => Number(value.toFixed(decimals));

export const formatSmallPrice = (value: number): string => {
  if (!value) return '0';

  const abs = Math.abs(value);

  if (abs < 0.000001) return value.toFixed(8);
  if (abs < 0.001) return value.toFixed(6);
  if (abs < 1) return value.toFixed(4);

  return value.toFixed(2);
};
